import { useEffect, useMemo, useState } from 'react';
import {
  CrownIcon,
  RankingIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';
import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

import {
  getLeaderboard,
  type LeaderboardEntry,
  type LeaderboardPeriod,
} from '../api';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

const PERIODS: { key: LeaderboardPeriod; tKey: string }[] = [
  { key: 'day', tKey: 'ranking.period_day' },
  { key: 'week', tKey: 'ranking.period_week' },
  { key: 'month', tKey: 'ranking.period_month' },
  { key: 'all', tKey: 'ranking.period_all' },
];

const MEDAL_TONE: Record<number, 'warning' | 'neutral' | 'info'> = {
  1: 'warning',
  2: 'neutral',
  3: 'info',
};

const MEDAL_GLYPH: Record<number, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
};

function fmtUsd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function displayName(entry: LeaderboardEntry, t: TFunction): string {
  if (entry.telegramUsername) return `@${entry.telegramUsername}`;
  if (entry.externalUserId) return entry.externalUserId.slice(0, 10);
  return t('ranking.anonymous');
}

export default function Ranking() {
  const t = useT();
  const auth = useAuth();
  useTelegramBackButton();
  useNavStackEntry({ label: t('ranking.breadcrumb') });

  const [period, setPeriod] = useState<LeaderboardPeriod>('week');
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const ready = auth.state === 'ready';
  const meExternalId = ready ? auth.auth.externalUserId : null;

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getLeaderboard(period, 20);
        if (!cancelled) setEntries(data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, period]);

  const eyebrow = useMemo(
    () => (
      <span className="inline-flex items-center gap-1.5">
        <RankingIcon weight="fill" className="size-3" />
        {t('ranking.eyebrow')}
      </span>
    ),
    [t],
  );

  if (auth.state === 'loading' || (loading && entries === null)) {
    return (
      <AuthLoadingScreen
        title={t('ranking.loading_title')}
        description={t('ranking.loading_desc')}
        shortDescription={t('ranking.loading_short')}
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: t('ranking.eyebrow'), color: 'bg-primary', delay: '0.4s' },
        ]}
      />
    );
  }

  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow={eyebrow} title={t('ranking.title')}>
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('common.auth_error', { error: '' })}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const list = entries ?? [];

  return (
    <ScreenShell
      eyebrow={eyebrow}
      title={t('ranking.title')}
      description={t('ranking.description')}
    >
      <NavBar />

      <PeriodPicker period={period} onChange={setPeriod} t={t} />

      {error && (
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('ranking.error_title')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!error && list.length === 0 ? (
        <Card>
          <CardHeader>
            <CardEyebrow>
              <CrownIcon weight="fill" className="size-3" />
              {t('ranking.empty_title')}
            </CardEyebrow>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('ranking.empty_desc')}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <div className="flex flex-col gap-2.5">
        {list.map((e) => (
          <RankingRow
            key={`${e.rank}-${e.externalUserId}`}
            entry={e}
            t={t}
            isMe={e.externalUserId === meExternalId}
          />
        ))}
      </div>
    </ScreenShell>
  );
}

function PeriodPicker({
  period,
  onChange,
  t,
}: {
  period: LeaderboardPeriod;
  onChange: (p: LeaderboardPeriod) => void;
  t: TFunction;
}) {
  return (
    <div className="bg-card/85 ring-foreground/10 flex gap-1 rounded-full p-1 ring-1 backdrop-blur-md">
      {PERIODS.map((p) => {
        const active = p.key === period;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.key)}
            className={cn(
              'flex-1 rounded-full px-3 py-1.5 text-[12px] font-semibold tracking-wide transition-colors',
              active
                ? 'bg-primary/20 text-foreground ring-1 ring-primary/30'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(p.tKey)}
          </button>
        );
      })}
    </div>
  );
}

function RankingRow({
  entry,
  t,
  isMe,
}: {
  entry: LeaderboardEntry;
  t: TFunction;
  isMe: boolean;
}) {
  const medal = MEDAL_GLYPH[entry.rank];
  const medalTone = MEDAL_TONE[entry.rank] ?? 'neutral';
  return (
    <div
      className={cn(
        'bg-card/90 ring-foreground/10 supports-backdrop-filter:bg-card/85 flex items-center gap-3 rounded-2xl p-3 ring-1 backdrop-blur-md',
        isMe && 'ring-primary/40 bg-primary/5',
      )}
    >
      <Badge tone={medalTone} className="min-w-[36px] justify-center text-[12px]">
        {medal ?? `#${entry.rank}`}
      </Badge>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-foreground truncate text-[14px] font-semibold">
            {displayName(entry, t)}
          </span>
          {isMe && (
            <Badge tone="primary" className="text-[10px]">
              {t('ranking.you_badge')}
            </Badge>
          )}
        </div>
        <span className="text-muted-foreground text-[11px] font-medium tracking-wide tabular-nums">
          {entry.tradeCount} {t('ranking.trades_label').toLowerCase()}
        </span>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        <span className="text-foreground font-mono text-[14px] font-semibold tabular-nums">
          {fmtUsd(entry.volumeUsd)}
        </span>
        <span className="text-muted-foreground text-[10px] font-semibold tracking-[0.14em] uppercase">
          {t('ranking.volume_label')}
        </span>
      </div>
    </div>
  );
}
